import { CanActivateFn, Router } from '@angular/router';
import { effect, inject, Injector, runInInjectionContext } from '@angular/core';

import { doc, Firestore, getDoc } from 'firebase/firestore';

import { AuthService } from '../firebase/auth.service';
import { FIREBASE_HANDLES } from '../firebase/firebase.providers';
import { parseJoinRequest } from '../orgs/join-request-parse';
import { shouldWaitForJoinRequest } from './join-request-routing';

/**
 * Resolves once the AuthService has reported its first auth state, so guards
 * don't make routing decisions on `undefined` (still loading) state.
 */
function waitForAuthInit(auth: AuthService, injector: Injector): Promise<void> {
  return waitForSignal(() => auth.initialized(), injector);
}

/**
 * Resolves once both the user AND their custom claims have been loaded.
 * Use this for role-based decisions — `initialized` only guarantees the
 * user identity is known, not that claims have been parsed yet.
 */
function waitForClaimsInit(auth: AuthService, injector: Injector): Promise<void> {
  return waitForSignal(() => auth.claimsInitialized(), injector);
}

function waitForSignal(predicate: () => boolean, injector: Injector): Promise<void> {
  if (predicate()) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    runInInjectionContext(injector, () => {
      const ref = effect(() => {
        if (predicate()) {
          ref.destroy();
          resolve();
        }
      });
    });
  });
}

/**
 * Guards routes that require an authenticated user.
 *
 * Behaviour:
 * - Waits for the initial Firebase auth state before deciding.
 * - On unauthenticated access, redirects to `/login` and stashes the
 *   originally-requested URL in the `returnUrl` query param so the login
 *   screen can deep-link back after a successful sign-in.
 */
export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);
  // Captured before the first await: `inject()` is unavailable after it.
  const db = inject(FIREBASE_HANDLES).firestore;

  await waitForAuthInit(auth, injector);

  if (!auth.isSignedIn()) {
    return router.createUrlTree(['/login'], {
      queryParams: state.url && state.url !== '/' ? { returnUrl: state.url } : undefined,
    });
  }

  // A self-serve signup holds a session before confirming its address,
  // because Firebase signs the new user in as part of account creation
  // and the `sendVerificationEmail` callable needs a signed-in caller.
  // That session carries no membership, so send it to the confirm screen
  // rather than to a dashboard with nothing in it.
  await waitForClaimsInit(auth, injector);
  if (isAwaitingEmailConfirmation(auth)) {
    return router.createUrlTree(['/signup']);
  }
  if (await shouldWaitOnJoinRequest(auth, db)) {
    return router.createUrlTree(['/join-request']);
  }
  return true;
};

/** A user without roles may be waiting on a join request. Anyone with roles costs no read. */
async function shouldWaitOnJoinRequest(auth: AuthService, db: Firestore): Promise<boolean> {
  const claims = auth.claims();
  const user = auth.user();
  if (!user || claims.internalAdmin === true || hasAnyOrgRole(claims.orgRoles)) {
    return false;
  }
  try {
    const snap = await getDoc(doc(db, 'users', user.uid));
    return shouldWaitForJoinRequest({
      internalAdmin: false,
      orgRoles: claims.orgRoles,
      emailVerified: user.emailVerified,
      joinRequest: parseJoinRequest(snap.data()?.['joinRequest']),
    });
  } catch {
    // An unreadable profile must not lock anyone out of the app.
    return false;
  }
}

function hasAnyOrgRole(orgRoles: Readonly<Record<string, string>> | undefined): boolean {
  return Object.keys(orgRoles ?? {}).length > 0;
}

/**
 * True only for a brand-new self-serve signup that has not confirmed its
 * address yet.
 *
 * The three conditions together are what keep this narrow. Gating on
 * `emailVerified` alone would bounce every existing user — none of the
 * accounts predating self-serve signup were ever verified, including the
 * owner of the only org — so anyone who already holds a membership, or
 * who is internal staff, passes through untouched.
 */
function isAwaitingEmailConfirmation(auth: AuthService): boolean {
  if (auth.user()?.emailVerified !== false) {
    return false;
  }
  // `signupPending` is written by `beforeUserCreated` and cleared when
  // membership is granted. An ABSENCE of orgRoles will not do as the
  // discriminator: an invited user can sign in before
  // `syncOrgMemberClaims` populates their claims — the same race
  // `pendingActivation` exists for — and if their address is unverified
  // too, keying off empty claims would divert them into the signup flow
  // and make the invitation flow unreachable.
  return auth.claims().signupPending === true;
}

/**
 * Guards `/signup`, which serves both the create-account form and the
 * confirm-your-address screen.
 *
 * Signed-out visitors get the form. A signed-in user who still needs to
 * confirm gets the confirm screen — `authGuard` sends them here. Anyone
 * else is already in and belongs in the app.
 */
export const signupGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  await waitForAuthInit(auth, injector);
  if (!auth.isSignedIn()) {
    return true;
  }

  await waitForClaimsInit(auth, injector);
  if (isAwaitingEmailConfirmation(auth)) {
    return true;
  }
  return router.createUrlTree(['/dashboard']);
};

/**
 * Guards routes that should only be visible to signed-out users (e.g. the
 * login screen). Authenticated users are bounced to the dashboard.
 */
export const publicOnlyGuard: CanActivateFn = async (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  await waitForAuthInit(auth, injector);

  if (!auth.isSignedIn()) {
    return true;
  }
  // The link that confirms a new address (Account → Security) lands on
  // `/login?emailChanged=1`. Firebase has revoked every session of the
  // account by then, but this browser still holds an ID token that looks
  // valid for up to an hour, and the person would be waved into the app
  // under the old address. Ask Firebase: a dead session is signed out
  // here and the page asks for the new address.
  if (route?.queryParamMap?.has('emailChanged') && !(await auth.confirmSessionAlive())) {
    return true;
  }
  return router.createUrlTree(['/dashboard']);
};

/**
 * Guards routes restricted to internal Ghosts staff/admins.
 *
 * Requires both an authenticated user and the `internalAdmin` custom claim.
 * Customer users are redirected to the app root rather than to /login (they
 * are signed in — they just lack the role).
 */
export const internalAdminGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  // Role decision — wait for claims, not just the user, otherwise an
  // internal admin can be redirected to `/` because `isInternalAdmin()`
  // is evaluated before `getIdTokenResult()` resolves.
  await waitForClaimsInit(auth, injector);

  if (!auth.isSignedIn()) {
    return router.createUrlTree(['/login'], {
      queryParams: state.url ? { returnUrl: state.url } : undefined,
    });
  }
  if (auth.isInternalAdmin()) {
    return true;
  }
  return router.createUrlTree(['/dashboard']);
};

/**
 * Guards the public landing page. Authenticated users are sent straight to
 * their dashboard — they've already "entered" the app.
 */
export const landingPageGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  await waitForAuthInit(auth, injector);

  if (auth.isSignedIn()) {
    return router.createUrlTree(['/dashboard']);
  }
  return true;
};

/**
 * Guards `/join-request`, the waiting screen. Signed-in only; anyone who
 * already holds a role belongs in the app.
 */
export const joinRequestGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  await waitForAuthInit(auth, injector);
  if (!auth.isSignedIn()) {
    return router.createUrlTree(['/login']);
  }
  await waitForClaimsInit(auth, injector);
  if (hasAnyOrgRole(auth.claims().orgRoles)) {
    return router.createUrlTree(['/dashboard']);
  }
  return true;
};
